import React from 'react';
import { Box, Text, TextInput } from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import { dateSectionTitle, dateWindowLabel } from '../filter.date.label';
import { filterSummary } from '../filter.summary';
import type { TypeDateSection, TypeFilterSection, TypeListSort } from '@/types/list/types.list.filter';
import FilterOption from './Filter.Option';
import FilterRow from './Filter.Row';

type Props = {
    sections: TypeFilterSection[];
    active: Record<string, string[]>;
    excluded: Record<string, boolean>;
    onToggle: (sectionId: string, value: string) => void;
    dates: TypeDateSection[];
    dateValues: Record<string, string>;
    sort?: TypeListSort;
    onOpen: (view: string) => void;
};

const RESULTS_MAX = 40;

const FilterRoot: React.FC<Props> = ({ sections, active, excluded, onToggle, dates, dateValues, sort, onOpen }) => {
    const [term, setTerm] = React.useState('');
    const needle = term.trim().toLowerCase();

    const results = needle
        ? sections.flatMap((section) => section.options
            .filter((option) => option.label.toLowerCase().includes(needle))
            .map((option) => ({ section, option }))).slice(0, RESULTS_MAX)
        : [];
    const sectionHits = needle ? sections.filter((section) => section.label.toLowerCase().includes(needle)) : [];
    const current = sort?.options.find((option) => option.value === sort.value);
    const direction = sort?.onToggleDirection ? (sort.descending ? current?.desc ?? 'decrescente' : current?.asc ?? 'crescente') : '';

    return (
        <>
            <Box className="bd-fp-search">
                <TextInput
                    size="sm"
                    value={term}
                    onChange={(event) => setTerm(event.currentTarget.value)}
                    onKeyDown={(event) => { if (event.key !== 'Escape') event.stopPropagation(); }}
                    placeholder={sections.length ? `Filtra per ${sections.slice(0, 2).map((section) => section.label.toLowerCase()).join(', ')}…` : 'Filtra…'}
                    leftSection={<IconSearch size={15} stroke={1.8} />}
                    aria-label="Cerca un filtro o un valore"
                    data-autofocus
                />
            </Box>

            {needle ? (
                <Box className="bd-fp-section" role="group" aria-label="Risultati">
                    {sectionHits.map((section) => (
                        <FilterRow
                            key={`section:${section.id}`}
                            label={section.label}
                            summary={filterSummary(section, active[section.id] ?? [], excluded[section.id])}
                            placeholder="Tutti"
                            count={active[section.id]?.length}
                            onOpen={() => onOpen(`s:${section.id}`)}
                        />
                    ))}
                    {results.map(({ section, option }) => (
                        <FilterOption
                            key={`${section.id}:${option.value}`}
                            option={option}
                            context={section.label}
                            single={section.single}
                            checked={(active[section.id] ?? []).includes(option.value)}
                            onToggle={() => onToggle(section.id, option.value)}
                        />
                    ))}
                    {!results.length && !sectionHits.length ? <Text size="xs" c="dimmed" py={6}>Nessun filtro o valore con “{term.trim()}”</Text> : null}
                </Box>
            ) : (
                <>
                    {sections.length ? (
                        <Box className="bd-fp-group" role="group" aria-label="Filtri">
                            {sections.map((section) => (
                                <FilterRow
                                    key={section.id}
                                    label={section.label}
                                    summary={filterSummary(section, active[section.id] ?? [], excluded[section.id])}
                                    placeholder="Tutti"
                                    count={active[section.id]?.length}
                                    onOpen={() => onOpen(`s:${section.id}`)}
                                />
                            ))}
                        </Box>
                    ) : null}
                    {sort && sort.options.length > 1 ? (
                        <Box className="bd-fp-group" role="group" aria-label="Ordinamento">
                            <FilterRow label="Ordina per" summary={[current?.label, direction].filter(Boolean).join(' · ')} placeholder="—" onOpen={() => onOpen('sort')} />
                        </Box>
                    ) : null}
                    {dates.length ? (
                        <Box className="bd-fp-group" role="group" aria-label="Date">
                            {dates.map((section) => (
                                <FilterRow
                                    key={section.id}
                                    label={dateSectionTitle(section.label)}
                                    summary={dateValues[section.id] ? dateWindowLabel(dateValues[section.id]) : ''}
                                    placeholder="Qualsiasi data"
                                    onOpen={() => onOpen(`d:${section.id}`)}
                                />
                            ))}
                        </Box>
                    ) : null}
                </>
            )}
        </>
    );
};

export default FilterRoot;
